import React, { useEffect } from "react";

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-[#1f2328] font-sans leading-relaxed">
      <h1 className="text-3xl md:text-4xl font-bold mb-1 text-[#1f2328]">
        Privacy Policy
      </h1>
      <p className="text-sm text-[#5a6169] mb-8">
        Last updated: September 23, 2026
      </p>

      <p className="mb-4">
        This Privacy Policy describes Our policies and procedures on the collection,
        use and disclosure of Your information when You use the Service and tells You
        about Your privacy rights and how the law protects You.
      </p>

      <p className="mb-8">
        We use Your Personal data to provide and improve the Service. By using the Service,
        You agree to the collection and use of information in accordance with this Privacy Policy.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Interpretation and Definitions
      </h2>

      <h3 className="text-xl font-semibold mt-6 mb-2 text-[#1f2328]">
        Interpretation
      </h3>
      <p className="mb-4">
        The words of which the initial letter is capitalized have meanings defined under
        the following conditions. The following definitions shall have the same meaning
        regardless of whether they appear in singular or in plural.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-2 text-[#1f2328]">
        Definitions
      </h3>
      <p className="mb-3">For the purposes of this Privacy Policy:</p>
      <ul className="list-disc pl-6 mb-8 space-y-2">
        <li>
          <strong>Account</strong> means a unique account created for You to access our
          Service or parts of our Service.
        </li>
        <li>
          <strong>Affiliate</strong> means an entity that controls, is controlled by or
          is under common control with a party, where &quot;control&quot; means ownership
          of 50% or more of the shares, equity interest or other securities entitled to vote
          for election of directors or other managing authority.
        </li>
        <li>
          <strong>Company</strong> (referred to as either &quot;the Company&quot;,
          &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot; in this Agreement) refers to
          Netstratum Inc, 24 Takolusa Dr, Holmdel, NJ 07733.
        </li>
        <li>
          <strong>Cookies</strong> are small files that are placed on Your computer,
          mobile device or any other device by a website, containing the details of Your
          browsing history on that website among its many uses.
        </li>
        <li>
          <strong>Country</strong> refers to: United States of America.
        </li>
        <li>
          <strong>Device</strong> means any device that can access the Service such as a
          computer, a cell phone or a digital tablet.
        </li>
        <li>
          <strong>Personal Data</strong> is any information that relates to an identified
          or identifiable individual.
        </li>
        <li>
          <strong>Service</strong> refers to the Website.
        </li>
        <li>
          <strong>Service Provider</strong> means any natural or legal person who processes
          the data on behalf of the Company. It refers to third-party companies or
          individuals employed by the Company to facilitate the Service, to provide the
          Service on behalf of the Company, to perform services related to the Service or to
          assist the Company in analyzing how the Service is used.
        </li>
        <li>
          <strong>SMS</strong> means a text message sent to or from a mobile telephone number.
        </li>
        <li>
          <strong>Usage Data</strong> refers to data collected automatically, either
          generated by the use of the Service or from the Service infrastructure itself
          (for example, the duration of a page visit).
        </li>
        <li>
          <strong>Website</strong> refers to Netstratum Inc, accessible from{" "}
          <a
            href="https://netstratum.com/"
            className="text-[#0b5cab] hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://netstratum.com/
          </a>
          .
        </li>
        <li>
          <strong>You</strong> means the individual accessing or using the Service, or the
          company, or other legal entity on behalf of which such individual is accessing or
          using the Service, as applicable.
        </li>
      </ul>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        SMS Communications
      </h2>
      <div className="border-l-4 border-[#1f2328] pl-5 py-2 my-4 bg-[#f8f9fa] rounded-r-lg space-y-3">
        <p>
          If You opt in to receive SMS messages from Netstratum Inc, We collect Your
          mobile telephone number and a record of Your consent. We use them only to send
          the messages You have consented to receive, which include Two-Factor
          Authentication (2FA) messages for account security, Customer Care communications
          to address inquiries and provide support, and account notifications regarding
          order status and links to product rates.
        </p>

        <p>
          <strong>How to opt in.</strong> You may opt in by checking the SMS consent box on
          our contact page at{" "}
          <a
            href="https://netstratum.com/contact-us"
            className="text-[#0b5cab] hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://netstratum.com/contact-us
          </a>{" "}
          and submitting the form, or by texting the keyword SUBSCRIBE to +1 732-320-9170.
          The consent box is not pre-checked, and consent is not a condition of purchase.
        </p>

        <p>
          <strong>Message frequency.</strong> Message frequency varies based on account
          activity.
        </p>

        <p>
          <strong>Costs.</strong> Message and data rates may apply. Charges are billed by,
          and payable to, Your mobile service provider.
        </p>

        <p>
          <strong>How to opt out.</strong> You may cancel at any time by replying STOP to
          any message You receive from Us. You will receive one confirmation message and will
          then receive no further messages.
        </p>

        <p>
          <strong>Help.</strong> Reply HELP to any message, email{" "}
          <a href="mailto:support@netstratum.com" className="text-[#0b5cab] hover:underline">
            support@netstratum.com
          </a>
          , or call +1 732-320-9170.
        </p>

        <p>
          <strong>Carriers.</strong> Mobile carriers are not liable for delayed or
          undelivered messages.
        </p>

        <p>
          <strong>No sharing of mobile information.</strong> Mobile opt-in information and
          Your consent to receive SMS notifications will not be shared, sold, rented, or
          disclosed to any third party or affiliate for marketing or promotional purposes.
          This exclusion applies to all sharing described elsewhere in this Privacy Policy,
          including sharing with Service Providers, Affiliates, business partners, and other
          users. Mobile information is shared only with the messaging providers that deliver
          the messages You have requested, and only for that purpose.
        </p>
      </div>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Collecting and Using Your Personal Data
      </h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Types of Data Collected
      </h3>

      <h4 className="text-lg font-semibold mt-4 mb-2 text-[#1f2328]">
        Personal Data
      </h4>
      <p className="mb-3">
        While using Our Service, We may ask You to provide Us with certain personally
        identifiable information that can be used to contact or identify You. Personally
        identifiable information may include, but is not limited to:
      </p>
      <ul className="list-disc pl-6 mb-6 space-y-1">
        <li>Email address</li>
        <li>First name and last name</li>
        <li>Phone number</li>
        <li>Usage Data</li>
      </ul>

      <h4 className="text-lg font-semibold mt-4 mb-2 text-[#1f2328]">
        Usage Data
      </h4>
      <p className="mb-3">
        Usage Data is collected automatically when using the Service.
      </p>
      <p className="mb-3">
        Usage Data may include information such as Your Device&apos;s Internet Protocol
        address (e.g. IP address), browser type, browser version, the pages of our Service
        that You visit, the time and date of Your visit, the time spent on those pages,
        unique device identifiers and other diagnostic data.
      </p>
      <p className="mb-3">
        When You access the Service by or through a mobile device, We may collect certain
        information automatically, including, but not limited to, the type of mobile device
        You use, Your mobile device unique ID, the IP address of Your mobile device, Your
        mobile operating system, the type of mobile Internet browser You use, unique device
        identifiers and other diagnostic data.
      </p>
      <p className="mb-6">
        We may also collect information that Your browser sends whenever You visit our Service
        or when You access the Service by or through a mobile device.
      </p>

      <h4 className="text-lg font-semibold mt-4 mb-2 text-[#1f2328]">
        Tracking Technologies and Cookies
      </h4>
      <p className="mb-3">
        We use Cookies and similar tracking technologies to track the activity on Our Service
        and store certain information. Tracking technologies used are beacons, tags, and
        scripts to collect and track information and to improve and analyze Our Service. The
        technologies We use may include:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>
          <strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on
          Your device. You can instruct Your browser to refuse all Cookies or to indicate
          when a Cookie is being sent. However, if You do not accept Cookies, You may not be
          able to use some parts of our Service. Unless You have adjusted Your browser
          setting so that it will refuse Cookies, our Service may use Cookies.
        </li>
        <li>
          <strong>Web Beacons.</strong> Certain sections of our Service and our emails may
          contain small electronic files known as web beacons (also referred to as clear
          gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to
          count users who have visited those pages or opened an email and for other related
          website statistics (for example, recording the popularity of a certain section and
          verifying system and server integrity).
        </li>
      </ul>
      <p className="mb-3">
        Cookies can be &quot;Persistent&quot; or &quot;Session&quot; Cookies. Persistent
        Cookies remain on Your personal computer or mobile device when You go offline, while
        Session Cookies are deleted as soon as You close Your web browser.
      </p>
      <p className="mb-4">
        We use both Session and Persistent Cookies for the purposes set out below:
      </p>

      <div className="space-y-4 mb-6 pl-4 border-l-2 border-gray-200">
        <div>
          <h5 className="font-bold text-[#1f2328]">Necessary / Essential Cookies</h5>
          <p className="text-sm text-gray-600">Type: Session Cookies | Administered by: Us</p>
          <p className="mt-1">
            Purpose: These Cookies are essential to provide You with services available
            through the Website and to enable You to use some of its features. They help to
            authenticate users and prevent fraudulent use of user accounts. Without these
            Cookies, the services that You have asked for cannot be provided, and We only use
            these Cookies to provide You with those services.
          </p>
        </div>

        <div>
          <h5 className="font-bold text-[#1f2328]">Cookies Policy / Notice Acceptance Cookies</h5>
          <p className="text-sm text-gray-600">Type: Session Cookies | Administered by: Us</p>
          <p className="mt-1">
            Purpose: These Cookies identify if users have accepted the use of cookies on the
            Website.
          </p>
        </div>

        <div>
          <h5 className="font-bold text-[#1f2328]">Functionality Cookies</h5>
          <p className="text-sm text-gray-600">Type: Session Cookies | Administered by: Us</p>
          <p className="mt-1">
            Purpose: These Cookies allow Us to remember choices You make when You use the
            Website, such as remembering Your login details or language preference. The purpose
            of these Cookies is to provide You with a more personal experience and to avoid
            You having to re-enter Your preferences every time You use the Website.
          </p>
        </div>
      </div>

      <p className="mb-8">
        For more information about the cookies We use and Your choices regarding cookies,
        please visit our Cookies Policy or the Cookies section of this Privacy Policy.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Use of Your Personal Data
      </h3>
      <p className="mb-3">
        The Company may use Personal Data for the following purposes:
      </p>
      <ul className="list-disc pl-6 mb-6 space-y-2">
        <li>
          <strong>To provide and maintain our Service</strong>, including to monitor the
          usage of our Service.
        </li>
        <li>
          <strong>To manage Your Account:</strong> to manage Your registration as a user of
          the Service. The Personal Data You provide can give You access to different
          functionalities of the Service that are available to You as a registered user.
        </li>
        <li>
          <strong>For the performance of a contract:</strong> the development, compliance
          and undertaking of the purchase contract for the products, items or services You
          have purchased or of any other contract with Us through the Service.
        </li>
        <li>
          <strong>To contact You:</strong> to contact You by email, telephone calls, SMS, or
          other equivalent forms of electronic communication, such as a mobile
          application&apos;s push notifications regarding updates or informative
          communications related to the functionalities, products or contracted services,
          including security updates, when necessary or reasonable for their implementation.
          SMS messages are sent only to those who have opted in as described in the SMS
          Communications section above.
        </li>
        <li>
          <strong>To provide You with news, special offers and general information</strong>{" "}
          about other goods, services and events which We offer that are similar to those that
          You have already purchased or enquired about, unless You have opted not to receive
          such information. We do not use mobile telephone numbers collected for SMS for
          these marketing or promotional communications.
        </li>
        <li>
          <strong>To manage Your requests:</strong> to attend and manage Your requests to Us.
        </li>
        <li>
          <strong>For business transfers:</strong> We may use Your information to evaluate
          or conduct a merger, divestiture, restructuring, reorganization, dissolution, or
          other sale or transfer of some or all of Our assets, whether as a going concern or
          as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data
          held by Us about our Service users is among the assets transferred.
        </li>
        <li>
          <strong>For other purposes:</strong> We may use Your information for other
          purposes, such as data analysis, identifying usage trends, determining the
          effectiveness of our promotional campaigns and to evaluate and improve our Service,
          products, services, marketing and Your experience.
        </li>
      </ul>

      <p className="mb-3 font-semibold">
        We may share Your personal information in the following situations:
      </p>
      <ul className="list-disc pl-6 mb-6 space-y-2">
        <li>
          <strong>With Service Providers:</strong> We may share Your personal information with
          Service Providers to monitor and analyze the use of our Service, and to contact You.
        </li>
        <li>
          <strong>For business transfers:</strong> We may share or transfer Your personal
          information in connection with, or during negotiations of, any merger, sale of
          Company assets, financing, or acquisition of all or a portion of Our business to
          another company.
        </li>
        <li>
          <strong>With Affiliates:</strong> We may share Your information with Our
          affiliates, in which case We will require those affiliates to honor this Privacy
          Policy. Affiliates include Our parent company and any other subsidiaries, joint
          venture partners or other companies that We control or that are under common
          control with Us.
        </li>
        <li>
          <strong>With business partners:</strong> We may share Your information with Our
          business partners to offer You certain products, services or promotions.
        </li>
        <li>
          <strong>With other users:</strong> when You share personal information or
          otherwise interact in the public areas with other users, such information may be
          viewed by all users and may be publicly distributed outside.
        </li>
        <li>
          <strong>With Your consent:</strong> We may disclose Your personal information for
          any other purpose with Your consent.
        </li>
      </ul>

      <p className="mb-8 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg text-amber-900 font-semibold">
        None of the sharing described above applies to mobile telephone numbers, SMS opt-in
        information, or SMS consent. Mobile opt-in information and consent to receive SMS
        notifications will not be shared, sold, rented, or disclosed to any third party or
        affiliate for marketing or promotional purposes.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Retention of Your Personal Data
      </h3>
      <p className="mb-3">
        The Company will retain Your Personal Data only for as long as is necessary for the
        purposes set out in this Privacy Policy. We will retain and use Your Personal Data to
        the extent necessary to comply with our legal obligations (for example, if We are
        required to retain Your data to comply with applicable laws), resolve disputes, and
        enforce our legal agreements and policies.
      </p>
      <p className="mb-6">
        The Company will also retain Usage Data for internal analysis purposes. Usage Data is
        generally retained for a shorter period of time, except when this data is used to
        strengthen the security or to improve the functionality of Our Service, or We are
        legally obligated to retain this data for longer time periods.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Transfer of Your Personal Data
      </h3>
      <p className="mb-3">
        Your information, including Personal Data, is processed at the Company&apos;s
        operating offices and in any other places where the parties involved in the
        processing are located. It means that this information may be transferred to — and
        maintained on — computers located outside of Your state, province, country or other
        governmental jurisdiction where the data protection laws may differ from those from
        Your jurisdiction.
      </p>
      <p className="mb-3">
        Your consent to this Privacy Policy followed by Your submission of such information
        represents Your agreement to that transfer.
      </p>
      <p className="mb-6">
        The Company will take all steps reasonably necessary to ensure that Your data is
        treated securely and in accordance with this Privacy Policy and no transfer of Your
        Personal Data will take place to an organization or a country unless there are
        adequate controls in place including the security of Your data and other personal
        information.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Delete Your Personal Data
      </h3>
      <p className="mb-3">
        You have the right to delete or request that We assist in deleting the Personal Data
        that We have collected about You.
      </p>
      <p className="mb-3">
        Our Service may give You the ability to delete certain information about You from
        within the Service.
      </p>
      <p className="mb-3">
        You may update, amend, or delete Your information at any time by signing in to Your
        Account, if You have one, and visiting the account settings section that allows You to
        manage Your personal information. You may also contact Us to request access to,
        correct, or delete any personal information that You have provided to Us.
      </p>
      <p className="mb-8">
        Please note, however, that We may need to retain certain information when We have a
        legal obligation or lawful basis to do so.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Disclosure of Your Personal Data
      </h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Business Transactions
      </h3>
      <p className="mb-6">
        If the Company is involved in a merger, acquisition or asset sale, Your Personal Data
        may be transferred. We will provide notice before Your Personal Data is transferred and
        becomes subject to a different Privacy Policy.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Law enforcement
      </h3>
      <p className="mb-6">
        Under certain circumstances, the Company may be required to disclose Your Personal Data
        if required to do so by law or in response to valid requests by public authorities (e.g.
        a court or a government agency).
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1f2328]">
        Other legal requirements
      </h3>
      <p className="mb-3">
        The Company may disclose Your Personal Data in the good faith belief that such action
        is necessary to:
      </p>
      <ul className="list-disc pl-6 mb-8 space-y-1">
        <li>Comply with a legal obligation</li>
        <li>Protect and defend the rights or property of the Company</li>
        <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
        <li>Protect the personal safety of Users of the Service or the public</li>
        <li>Protect against legal liability</li>
      </ul>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Security of Your Personal Data
      </h2>
      <p className="mb-8">
        The security of Your Personal Data is important to Us, but remember that no method of
        transmission over the Internet, or method of electronic storage is 100% secure. While
        We strive to use commercially acceptable means to protect Your Personal Data, We
        cannot guarantee its absolute security.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Children&apos;s Privacy
      </h2>
      <p className="mb-3">
        Our Service does not address anyone under the age of 13. We do not knowingly collect
        personally identifiable information from anyone under the age of 13. If You are a parent
        or guardian and You are aware that Your child has provided Us with Personal Data, please
        contact Us. If We become aware that We have collected Personal Data from anyone under the
        age of 13 without verification of parental consent, We take steps to remove that
        information from Our servers.
      </p>
      <p className="mb-8">
        If We need to rely on consent as a legal basis for processing Your information and Your
        country requires consent from a parent, We may require Your parent&apos;s consent before
        We collect and use that information.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Links to Other Websites
      </h2>
      <p className="mb-3">
        Our Service may contain links to other websites that are not operated by Us. If You
        click on a third party link, You will be directed to that third party&apos;s site. We
        strongly advise You to review the Privacy Policy of every site You visit.
      </p>
      <p className="mb-8">
        We have no control over and assume no responsibility for the content, privacy policies
        or practices of any third party sites or services.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Changes to this Privacy Policy
      </h2>
      <p className="mb-3">
        We may update Our Privacy Policy from time to time. We will notify You of any changes
        by posting the new Privacy Policy on this page.
      </p>
      <p className="mb-3">
        We will let You know via email and/or a prominent notice on Our Service, prior to the
        change becoming effective and update the &quot;Last updated&quot; date at the top of
        this Privacy Policy.
      </p>
      <p className="mb-8">
        You are advised to review this Privacy Policy periodically for any changes. Changes to
        this Privacy Policy are effective when they are posted on this page.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-[#1f2328] border-b pb-2">
        Contact Us
      </h2>
      <p className="mb-3">
        If you have any questions about this Privacy Policy, You can contact us:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>
          By email:{" "}
          <a href="mailto:info@netstratum.com" className="text-[#0b5cab] hover:underline">
            info@netstratum.com
          </a>
        </li>
        <li>
          For SMS support, by email:{" "}
          <a href="mailto:support@netstratum.com" className="text-[#0b5cab] hover:underline">
            support@netstratum.com
          </a>
        </li>
        <li>
          By visiting this page on our website:{" "}
          <a
            href="https://netstratum.com/"
            className="text-[#0b5cab] hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://netstratum.com/
          </a>
        </li>
        <li>By phone number: +1 (732) 320-9170</li>
        <li>By mail: Netstratum Inc, 24 Takolusa Dr, Holmdel, NJ 07733</li>
      </ul>
    </div>
  );
}

export default PrivacyPolicy;